import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useLayoutEffect,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import Grid from "@mui/material/Grid";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { ChannelActions, NotificationActions, RootStateType } from "@Store";
import { theme } from "@Styled";
import { PollType } from "@Interfaces/Entities/Poll";
import {
    Dialog,
    DialogItem,
    DialogItemWrapper,
    DialogQuestionWrapper,
    EmojiButton,
    QuestionWarpper,
    StyledActionsWrapper,
    StyledButton,
    StyledCardActions,
    StyledCardContent,
    StyledDeleteOption,
    StyledForm,
    StyledGrid,
    StyledOption,
    StyledOptionField,
    StyledOptionWrapper,
    StyledQuestionField,
    StyledSubmitButton,
    MultiVotingHint,
} from "./PollGeneratorForm.styled";
import { AddOptionIcon } from "../../Icons/Icons";
import { Box, Card, Hidden, Tab, Tabs } from "@mui/material";
import PollImageUploader from "./PollImage/PollImageUploader";
import PollTextField from "@Components/PollTextField";
import { NpsForm } from "@Components/Nps";
import { useIndexedDB } from "@Services/indexedDbClient";
import { isIndexDbExists } from "@Utils/indexDb";
import { postMediaImage } from "@Services/API/Media";
import { postPollTextField } from "@Services/API/PollTextField";
import Picker, { IEmojiData } from "emoji-picker-react";
import useClickOutside from "@Hooks/useClickOutside";
import AddReactionIcon from "@mui/icons-material/Mood";
import PollIcon from "@mui/icons-material/Poll";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import BarChartIcon from "@mui/icons-material/BarChart";
import { IChoice, IPoll } from "@Interfaces";
import {
    getOptionsRules,
    imagesOptionsRules,
    MAX_CHOICE_LENGTH,
    MAX_QUESTION_LENGTH,
    getQuestionRules,
} from "./formUtils";
import useSuggestedChoices from "@Hooks/useSuggestedChoices";
import AutofillChoices from "./AutofillChoices/AutofillChoices";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { useTranslation } from "react-i18next";

interface PropsEmojiItem {
    key: number;
    showItemPicker: boolean;
}

type FormValues = {
    choices: Array<{
        txt: string;
        img: string | null;
    }>;
    publicUrl?: string;
    question: string;
};

type PollGeneratorTypes = {
    channelId: string;
    poll?: IPoll;
};

const MAX_POLL_CHOICES = 16;
const MIN_POLL_CHOICES = 2;
const OPTIONS_LETTER = Array.from({ length: MAX_POLL_CHOICES }, (_, index) =>
    String.fromCharCode(65 + index)
);
const TABS = {
    POLL: 0,
    TEXT_FIELD: 1,
    MULTI_VOTING: 2,
    NPS: 3,
} as const;
const TEXTFIELD_MAX_LENGTH = 140;
const MULTI_VOTING_TAB_INDEX = TABS.MULTI_VOTING;

const buildChoicesFromPoll = (choices: IChoice[]) =>
    choices.map((choice) => ({
        txt: choice.txt,
        img: typeof choice.img === "string" ? choice.img : null,
    }));

export const PollCreatorDialog: React.FC<PollGeneratorTypes> = ({
    channelId,
    poll,
}) => {
    const { t } = useTranslation();
    const optionsRules = getOptionsRules(t);
    const questionRules = getQuestionRules(t);
    const dispatch = useDispatch<any>();
    const { getByIndex, update, deleteRecord } = useIndexedDB("draftPoll");

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, suggestedChoices] = useSuggestedChoices();

    const [isRequiredImgs, setIsRequiredImgs] = useState(false);
    const [questionState, setQuestionState] = useState("");
    const [choicesState, setChoicesState] = useState<
        Array<{ txt: string; img: string | null }>
    >([
        { txt: "", img: null },
        { txt: "", img: null },
    ]);
    const [storedImages, setStoredImages] = useState<string[]>([]);
    const [, setChoicePreviewUrls] = useState<Array<string | null>>([]);
    const isEditMode = Boolean(poll);
    const submitLabel = isEditMode ? t("poll.save_changes") : t("poll.ask");
    const [questionLength, setQuestionLength] = useState(0);
    const [choicesLenght, setChoicesLenght] = useState<number[]>(
        Array(MAX_POLL_CHOICES).fill(0)
    );
    const [activeTab, setActiveTab] = useState<number>(TABS.POLL);

    const mountedRef = useRef(true);
    const mountedItemRef = useRef(true);

    const [showEmojis, setShowEmojis] = useState(false);
    const picker = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const pickerArrayItem = useRef<Array<HTMLDivElement | null>>([]);
    const [showEmojisItem, setShowEmojisItem] = useState<PropsEmojiItem[]>([]);
    const user = useSelector((state: RootStateType) => state.auth.user);

    const {
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        getValues,
        watch,
        reset,
    } = useForm<FormValues>({
        defaultValues: {
            choices: choicesState,
        },
        mode: "onSubmit",
    });

    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: "choices",
    });

    const handleTabChange = (
        _event: React.ChangeEvent<{}>,
        newValue: number
    ) => {
        setActiveTab(newValue);
    };

    // Persist the prompt and refresh the channel polls/text-fields for the UI.
    const handleAskTextField = useCallback(
        async (question: string) => {
            try {
                await postPollTextField({
                    channel_id: channelId,
                    question,
                });
                await dispatch(ChannelActions.fetchChannelPolls(channelId));
            } catch (error) {
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: t("home.tabs.textfield_export_error"),
                    })
                );
            } finally {
                dispatch(dialogActions.hideDialog());
                setActiveTab(TABS.POLL);
            }
        },
        [channelId, dispatch, t]
    );

    const handleNPSPollCreated = useCallback(
        async (poll: IPoll) => {
            try {
                await dispatch(ChannelActions.fetchChannelPolls(channelId));
            } catch (error) {
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: t("dialogs.poll_creation.nps.error"),
                    })
                );
            } finally {
                dispatch(dialogActions.hideDialog());
                setActiveTab(TABS.POLL);
            }
        },
        [channelId, dispatch, t]
    );

    const addEmojiToQuestion = (
        event: React.MouseEvent<Element, MouseEvent>,
        emojiObject: IEmojiData
    ) => {
        event.preventDefault();
        let question = getValues("question");
        setValue("question", `${question}${emojiObject.emoji}`);
        dismissEmojiPicker();
    };

    const toggleQuestionPicker = () => {
        setShowEmojis(!showEmojis);
    };

    const togglePickerItem = (index: number) => {
        setCurrentIndex(index);
        if (!showEmojisItem.some((emoji) => emoji.key === index)) {
            const newState = [
                ...showEmojisItem,
                { key: index, showItemPicker: true },
            ];
            setShowEmojisItem(newState);
        } else {
            const newState = showEmojisItem.map((obj: PropsEmojiItem) => {
                if (obj.key === index) {
                    return { ...obj, showItemPicker: true };
                } else {
                    return { ...obj, showItemPicker: false };
                }
            });
            setShowEmojisItem(newState);
        }
    };

    const addEmojiToChoice = (
        event: React.MouseEvent<Element, MouseEvent>,
        emojiObject: IEmojiData
    ) => {
        event.preventDefault();
        const choice = getValues(`choices.${currentIndex}.txt`);
        setValue(
            `choices.${currentIndex}.txt`,
            `${choice}${emojiObject.emoji}`
        );
        dismissPickerItem();
    };

    const handleInitialState = (
        choices: { txt: string; img: string | null }[]
    ) => {
        const initialStateItems: PropsEmojiItem[] = Array.from(
            { length: choices.length },
            (_, index) => ({ key: index, showItemPicker: false })
        );
        setShowEmojisItem(initialStateItems);
    };

    const dismissEmojiPicker = useCallback(() => {
        setShowEmojis(false);
    }, [setShowEmojis]);

    useClickOutside(picker, dismissEmojiPicker, 0);

    const dismissPickerItem = useCallback(() => {
        const newState = showEmojisItem.map((obj: PropsEmojiItem) => {
            return { ...obj, showItemPicker: false };
        });
        setShowEmojisItem(newState);
    }, [showEmojisItem, setShowEmojisItem]);

    useClickOutside(pickerArrayItem, dismissPickerItem, currentIndex);

    const watchFields = watch(
        Array.from(
            { length: MAX_POLL_CHOICES },
            (_, index) => `choices.${index}.txt` as const
        )
    );

    const watchQuestionFields = watch([`question`]);

    const getStoredDataAndPerformAction = async (
        callback: (data: any) => void
    ) => {
        const isUserStored = await isIndexDbExists(user.id);

        if (!isUserStored) return;

        const storedData = await getByIndex("channel_id", channelId);

        callback(storedData);
    };

    useLayoutEffect(() => {
        if (poll) {
            return;
        }

        const initiateData = (storedData: any) => {
            if (storedData) {
                // set question's state initial value
                setQuestionState(storedData.question);
                // set question's value initial value for react-hook-form needs
                setValue("question", storedData.question, {
                    shouldDirty: true,
                });
                const restoredChoices = Array.isArray(storedData.draftChoices)
                    ? storedData.draftChoices.slice(0, MAX_POLL_CHOICES)
                    : [];

                while (restoredChoices.length < MIN_POLL_CHOICES) {
                    restoredChoices.push({ txt: "", img: null });
                }

                setChoicesState(restoredChoices);
                replace(restoredChoices);
                handleInitialState(restoredChoices);

                // if there are stored images set it as default value || null
                let restoredImages: string[] = [];
                restoredChoices.forEach((_: any, i: number) => {
                    setValue(`choices.${i}.img`, restoredChoices[i].img);
                    restoredImages.push(restoredChoices[i].img || null);
                });
                // if at least an image has been stored, set images as required for all choices
                if (restoredImages.some((e: string) => e !== null)) {
                    setIsRequiredImgs(true);
                    setStoredImages(restoredImages);
                }
            }
        };

        getStoredDataAndPerformAction(initiateData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [replace, setValue]);

    useEffect(() => {
        if (!poll) {
            return;
        }

        // Set the active tab based on poll type FIRST
        if (poll.poll_type === "nps") {
            setActiveTab(TABS.NPS);
            // Don't load NPS poll data into the main form, it's handled by CreateNPSPoll
            return;
        } else if (poll.poll_type === "multivoting" || poll.multi_voting) {
            setActiveTab(TABS.MULTI_VOTING);
            // Continue to load multi-voting data below
        } else {
            setActiveTab(TABS.POLL);
            // Continue to load standard poll data below
        }

        const initialChoices = buildChoicesFromPoll(poll.choices || []);

        setQuestionState(poll.question);
        setChoicesState(initialChoices);
        setStoredImages(
            initialChoices.reduce<string[]>((acc, choice) => {
                const img = choice.img;
                if (img !== null && typeof img === "string") {
                    acc.push(img);
                }
                return acc;
            }, [])
        );
        setIsRequiredImgs(
            initialChoices.filter((choice) => !!choice.img).length > 0
        );

        reset({
            question: poll.question,
            choices: initialChoices,
        });

        setValue("question", poll.question, { shouldDirty: true });
        setValue("choices", initialChoices);
        handleInitialState(
            initialChoices as Array<{ txt: string; img: string | null }>
        );

        setChoicePreviewUrls(
            (poll.choices || []).map((choice) => {
                if (!choice?.img) return null;
                if (typeof choice.img === "object") {
                    return choice.img.public_url || null;
                }
                return null;
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [poll, reset, setValue]);

    useEffect(() => {
        const { question, choices } = watch();
        handleDb(question, choices, channelId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watch]);

    useEffect(() => {
        return () => {
            handleChoicesTextLenght();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchFields]);

    useEffect(() => {
        return () => {
            handleQuestionTextLenght();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchQuestionFields]);

    useEffect(() => {
        if (mountedRef.current) dismissEmojiPicker();
        return () => {
            mountedRef.current = false; // clean up function
        };
    }, [dismissEmojiPicker]);

    useEffect(() => {
        if (mountedItemRef.current) dismissPickerItem();
        return () => {
            mountedItemRef.current = false; // clean up function
        };
    }, [dismissPickerItem]);

    useEffect(() => {
        if (suggestedChoices && suggestedChoices?.length > 0) {
            const targetCount = Math.min(
                MAX_POLL_CHOICES,
                Math.max(fields.length, suggestedChoices.length)
            );

            if (fields.length < targetCount) {
                const toAppend = targetCount - fields.length;
                for (let i = 0; i < toAppend; i++) {
                    append({ txt: "", img: null });
                }
                setChoicePreviewUrls((prev) => [
                    ...prev,
                    ...Array(toAppend).fill(null),
                ]);
            }

            for (let index = 0; index < targetCount; index++) {
                if (suggestedChoices[index]) {
                    setValue(`choices.${index}.txt`, suggestedChoices[index]);
                } else {
                    setValue(`choices.${index}.txt`, "");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestedChoices]);

    const handleDb = async (
        question: string,
        choices: { txt: string; img: string | null }[],
        channelId: string
    ) => {
        const isUserStored = await isIndexDbExists(user.id);

        if (!isUserStored) return;

        try {
            await update({
                id: channelId,
                question: question || "",
                channel_id: channelId,
                draftChoices: choices || [],
            });
        } catch (error) {
            // Avoid crashing the UI if draft persistence fails.
            // eslint-disable-next-line no-console
            console.error("Failed to persist draft poll", error);
        }
    };

    const clearDraftPoll = async (channelId: string) => {
        const isUserStored = await isIndexDbExists(user.id);

        if (!isUserStored) return;

        try {
            await deleteRecord(channelId);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to clear draft poll", error);
        }
    };

    const handleAppendOption = () => {
        if (fields.length < MAX_POLL_CHOICES) {
            append({ txt: "", img: null });
            setChoicePreviewUrls((prev) => [...prev, null]);
        }
    };

    const handleOptionRemove = (index: number) => {
        if (fields.length > 2) {
            remove(index);
            setChoicePreviewUrls((prev) =>
                prev.filter((_, idx) => idx !== index)
            );
        }
    };

    const handleDraftImage = async (
        formData: () => Promise<FormData>,
        name: string
    ) => {
        const body = await formData(); // gets formData from canvas
        const { image_id } = await postMediaImage(body);

        setValue(name as any, image_id);
        setIsRequiredImgs(true);
    };

    const deleteOptionButtonComp = (index: number) => (
        <StyledDeleteOption
            aria-label="delete option"
            type="button"
            onClick={() => handleOptionRemove(index)}
        >
            <RemoveCircleIcon
                htmlColor={theme.palette.common.pink}
                sx={{ fontSize: "1.2rem" }}
            />
        </StyledDeleteOption>
    );

    const checkRequiredImgs = () => {
        const values = getValues();
        const choices = values.choices;
        const choicesImgs = choices.map((choice: any) => choice.img);

        const isFulled = choicesImgs.filter(
            (item: string) => item === "" || item === null
        ).length;

        if (isFulled === choicesImgs.length || isFulled === 0) {
            setIsRequiredImgs(false);
        } else {
            setIsRequiredImgs(true);
        }
    };

    const handleDraftImageRemove = (name: string) => {
        setValue(name as any, null);
        checkRequiredImgs();
    };

    const handleQuestionTextLenght = () => {
        const values = getValues();
        const question = values.question;
        setQuestionLength(question?.length ?? 0);
    };

    const handleChoicesTextLenght = () => {
        const values = getValues();
        const choices = values.choices;
        const choicesLenghtArray = choices
            ? choices.map((choice: any) => choice?.txt?.length)
            : [];
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        JSON.stringify(choicesLenghtArray) !== JSON.stringify(choicesLenght)
            ? setChoicesLenght(choicesLenghtArray)
            : null;
    };

    const onSubmit = async ({ question, choices }: FormValues) => {
        const isMultiVoting = activeTab === MULTI_VOTING_TAB_INDEX;

        if (isEditMode && poll) {
            await dispatch(
                ChannelActions.editPoll(poll._id, {
                    question,
                    choices,
                    poll_type: poll.poll_type || PollType.STANDARD,
                }) as any
            );
            // Refetch channel polls to show updated poll
            await dispatch(ChannelActions.fetchChannelPolls(channelId) as any);
        } else {
            await dispatch(
                ChannelActions.createPoll({
                    question,
                    choices,
                    channel_id: channelId,
                    multi_voting: isMultiVoting,
                    poll_type: isMultiVoting
                        ? PollType.MULTIVOTING
                        : PollType.STANDARD,
                }) as any
            );
            await clearDraftPoll(channelId);
        }

        dispatch(dialogActions.hideDialog());
    };

    const OptionItem = (index: number) => {
        return (
            <StyledOptionWrapper>
                <StyledOption>
                    <Controller
                        render={({ field: { ref, ...field } }) => (
                            <StyledOptionField
                                {...field}
                                inputRef={ref}
                                value={field.value ?? ""}
                                onChange={(event) =>
                                    field.onChange(event.target.value)
                                }
                                variant="standard"
                                error={
                                    !!errors.choices && !!errors.choices[index]
                                }
                                fullWidth
                                helperText={
                                    <>
                                        {`${choicesLenght[index]}/${MAX_CHOICE_LENGTH}`}
                                        <br />
                                        {optionErrors(errors, index)}
                                    </>
                                }
                                label={`${t("poll.option")} ${
                                    OPTIONS_LETTER[index]
                                }`}
                            />
                        )}
                        control={control}
                        defaultValue={choicesState[index]?.txt || ""}
                        name={`choices.${index}.txt`}
                        rules={optionsRules}
                    />
                </StyledOption>
                <Controller
                    control={control}
                    defaultValue={choicesState[index]?.img || null}
                    name={`choices.${index}.img`}
                    render={({ field: { name } }) => (
                        <PollImageUploader
                            onSetBuffer={(e) => handleDraftImage(e, name)}
                            onDraftImageRemove={() =>
                                handleDraftImageRemove(name)
                            }
                            defaultImage={
                                storedImages.length !== 0
                                    ? storedImages[index]
                                    : null
                            }
                        />
                    )}
                    rules={imagesOptionsRules(isRequiredImgs)}
                />
                <Hidden xsDown>
                    <DialogItemWrapper
                        ref={(el: HTMLDivElement) => {
                            pickerArrayItem.current[index] = el;
                        }}
                        className={"item-" + index}
                    >
                        <DialogItem>
                            {showEmojisItem?.find(
                                (emojItem: PropsEmojiItem) =>
                                    emojItem.key === index
                            )?.showItemPicker && (
                                <Picker onEmojiClick={addEmojiToChoice} />
                            )}
                        </DialogItem>
                        <EmojiButton onClick={() => togglePickerItem(index)}>
                            <AddReactionIcon />
                        </EmojiButton>
                    </DialogItemWrapper>
                </Hidden>
                {index > 1 && deleteOptionButtonComp(index)}
            </StyledOptionWrapper>
        );
    };

    const optionErrors = (errors: any, index: number) => {
        if (!!errors.choices) {
            if (errors?.choices[index]) {
                return (
                    errors?.choices[index]?.txt?.message ||
                    errors?.choices[index]?.img?.message
                );
            }
        }

        return "";
    };

    return (
        <>
            <div
                style={{
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    marginBottom: "16px",
                    margin: "0 auto",
                    maxWidth: "960px",
                    padding: "0 24px",
                    boxSizing: "border-box",
                }}
            >
                <Tabs
                    value={activeTab}
                    onChange={isEditMode ? () => {} : handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    aria-label={t("dialogs.poll_creation.tabs.aria_label")}
                    variant="fullWidth"
                    sx={{
                        "& .MuiTabs-indicator": {
                            height: 3,
                            borderRadius: 3,
                        },
                    }}
                >
                    <Tab
                        icon={<PollIcon />}
                        label={t("dialogs.poll_creation.tabs.poll")}
                        sx={{ minHeight: 48, textTransform: "none" }}
                        disableRipple
                    />
                    <Tab
                        icon={<TextFieldsIcon />}
                        label={t("dialogs.poll_creation.tabs.textfield")}
                        sx={{ minHeight: 48, textTransform: "none" }}
                        disableRipple
                    />
                    <Tab
                        icon={<DoneAllIcon />}
                        label={t("dialogs.poll_creation.tabs.multi_voting")}
                        sx={{ minHeight: 48, textTransform: "none" }}
                        disableRipple
                    />
                    <Tab
                        icon={<BarChartIcon />}
                        label={t("dialogs.poll_creation.tabs.nps")}
                        sx={{ minHeight: 48, textTransform: "none" }}
                        disableRipple
                    />
                </Tabs>
            </div>
            {(activeTab === TABS.POLL ||
                activeTab === MULTI_VOTING_TAB_INDEX) && (
                <StyledForm
                    className="PollGeneratorForm"
                    style={{
                        margin: "0 auto",
                        width: "100%",
                        maxWidth: "960px",
                        minHeight: "420px",
                        padding: "0 24px 24px",
                        boxSizing: "border-box",
                    }}
                    noValidate
                    autoComplete="off"
                    encType="multipart/form-data"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Card sx={{ boxShadow: "none" }}>
                        <StyledCardContent>
                            {activeTab === MULTI_VOTING_TAB_INDEX && (
                                <MultiVotingHint>
                                    <DoneAllIcon fontSize="small" />
                                    <span>
                                        {t(
                                            "dialogs.poll_creation.multi_voting_hint"
                                        )}
                                    </span>
                                </MultiVotingHint>
                            )}
                            <QuestionWarpper>
                                <Controller
                                    render={({ field: { ref, ...field } }) => (
                                        <StyledQuestionField
                                            {...field}
                                            inputRef={ref}
                                            value={field.value ?? ""}
                                            onChange={(event) =>
                                                field.onChange(
                                                    event.target.value
                                                )
                                            }
                                            variant="standard"
                                            error={!!errors.question}
                                            fullWidth
                                            helperText={
                                                <>
                                                    {`${questionLength}/${MAX_QUESTION_LENGTH}`}
                                                    <br />
                                                    {!!errors.question &&
                                                        errors.question.message}
                                                </>
                                            }
                                            label={t("poll.question")}
                                        />
                                    )}
                                    control={control}
                                    defaultValue={questionState}
                                    name={`question`}
                                    rules={questionRules}
                                />

                                <AutofillChoices
                                    question={getValues("question")}
                                ></AutofillChoices>

                                <Hidden xsDown>
                                    <DialogQuestionWrapper ref={picker}>
                                        <Dialog
                                            style={{
                                                position: "fixed",
                                                right: "auto",
                                                top: "auto",
                                            }}
                                        >
                                            {showEmojis && (
                                                <Picker
                                                    onEmojiClick={
                                                        addEmojiToQuestion
                                                    }
                                                />
                                            )}
                                        </Dialog>
                                        <EmojiButton
                                            onClick={toggleQuestionPicker}
                                        >
                                            <AddReactionIcon />
                                        </EmojiButton>
                                    </DialogQuestionWrapper>
                                </Hidden>
                            </QuestionWarpper>
                            <StyledGrid
                                container
                                spacing={0}
                                sx={{
                                    m: 0,
                                    width: "100%",
                                    "@media (max-width: 900px)": {
                                        margin: "20px 0",
                                    },
                                }}
                            >
                                {fields.map((field, index) => {
                                    return (
                                        <Grid
                                            container
                                            item
                                            key={field.id}
                                            sm={6}
                                            sx={{
                                                pl: {
                                                    xs: 0,
                                                    sm: index % 2 === 0 ? 0 : 1,
                                                },
                                                pr: {
                                                    xs: 0,
                                                    sm: index % 2 === 0 ? 1 : 0,
                                                },
                                            }}
                                        >
                                            {OptionItem(index)}
                                        </Grid>
                                    );
                                })}
                            </StyledGrid>
                        </StyledCardContent>

                        <StyledCardActions>
                            <StyledActionsWrapper>
                                <StyledButton
                                    aria-label="add"
                                    onClick={handleAppendOption}
                                    sx={{
                                        pl: 0,
                                        alignSelf: "flex-end",
                                        mb: "-3px",
                                    }}
                                >
                                    <AddOptionIcon
                                        color={theme.palette.common.pink}
                                    />
                                    <span>{t("poll.add_options")}</span>
                                </StyledButton>
                                <StyledSubmitButton
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                >
                                    {submitLabel}
                                </StyledSubmitButton>
                            </StyledActionsWrapper>
                        </StyledCardActions>
                    </Card>
                </StyledForm>
            )}
            {activeTab === TABS.TEXT_FIELD && (
                <div
                    style={{
                        margin: "0 auto",
                        width: "100%",
                        maxWidth: "960px",
                        minHeight: "420px",
                        padding: "0 24px 24px",
                        boxSizing: "border-box",
                    }}
                >
                    <Card
                        sx={{
                            boxShadow: "none",
                            mt: 2,
                        }}
                    >
                        <StyledCardContent>
                            <Box
                                sx={{
                                    "& .poll-textfield-form": {
                                        width: "100%",
                                    },
                                    "& .poll-textfield-prompt": {
                                        mb: 1,
                                        fontWeight: 700,
                                        color: "grey.800",
                                    },
                                    "& .poll-textfield-input": {
                                        pt: "0.15rem",
                                        pb: "0.15rem",
                                        lineHeight: 1.4,
                                    },
                                    "& .poll-textfield-actions": {
                                        mt: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    },
                                }}
                            >
                                <PollTextField
                                    question={t(
                                        "dialogs.poll_creation.textfield.prompt"
                                    )}
                                    placeholder={t(
                                        "dialogs.poll_creation.textfield.placeholder"
                                    )}
                                    submitLabel={t(
                                        "dialogs.poll_creation.textfield.ask_button"
                                    )}
                                    required
                                    maxLength={TEXTFIELD_MAX_LENGTH}
                                    rows={1}
                                    validateEmptyMessage={t(
                                        "dialogs.poll_creation.textfield.error_empty"
                                    )}
                                    counterFormatter={(count, limit) =>
                                        t(
                                            "dialogs.poll_creation.textfield.counter",
                                            {
                                                count,
                                                limit,
                                            }
                                        )
                                    }
                                    formClassName="poll-textfield-form"
                                    promptClassName="poll-textfield-prompt"
                                    inputClassName="poll-textfield-input"
                                    actionsClassName="poll-textfield-actions"
                                    textFieldProps={{ maxRows: 4 }}
                                    onSubmit={handleAskTextField}
                                />
                            </Box>
                        </StyledCardContent>
                    </Card>
                </div>
            )}
            {activeTab === TABS.NPS && (
                <div
                    style={{
                        margin: "0 auto",
                        width: "100%",
                        maxWidth: "960px",
                        minHeight: "420px",
                        padding: "0 24px 24px",
                        boxSizing: "border-box",
                    }}
                >
                    <Card
                        sx={{
                            boxShadow: "none",
                            mt: 2,
                        }}
                    >
                        <StyledCardContent>
                            <NpsForm
                                channelId={channelId}
                                onPollCreated={handleNPSPollCreated}
                                loading={false}
                                editingPoll={poll}
                            />
                        </StyledCardContent>
                    </Card>
                </div>
            )}
        </>
    );
};

//export default React.memo(PollGeneratorForm);

export default {
    content: PollCreatorDialog,
    title: "Create Poll",
    showActons: false,
    backDropClick: false,
};
